import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../app-deps";
import { signOut } from "../core/ops/auth-ops";
import { loadCapabilities } from "../core/ops/capability-ops";
import { loadProviders } from "../core/ops/provider-ops";
import { isStreamLive, loadPendingCarts } from "../core/ops/shell-ops";
import { loadLocations, loadStores } from "../core/ops/store-ops";
import { chooseWorkspace, switchWorkspace } from "../core/ops/workspace-ops";
import { resolveOnboardingStep } from "../core/primitives/onboarding";
import { activeLocations, implicitWorkspace, isWorkspaceValid, shopEntryId } from "../core/primitives/workspace";
import { SetupChecklistController } from "../sections/setup-checklist/setup-checklist-controller";
import { setSentryStore } from "../sentry";
import { ShellActions, ShellState } from "./shell-model";

export type ShellDeps = Pick<
  AppDeps,
  | "client"
  | "session"
  | "authHandler"
  | "storeRepository"
  | "locationsRepository"
  | "pendingCartsRepository"
  | "providersRepository"
  | "capabilitiesRepository"
  | "reviewQueueRepository"
  | "customersRepository"
  | "broadcastStudio"
>;

export interface ShellRouting {
  onSignedOut(): void;
  /** Leaves the customer a Customer page carries: the one served at the counter left. */
  leaveCustomer?(): void;
}

/** A stream flips to LIVE from the video provider's webhook, so it has to be noticed; slow, because nobody is waiting on it. */
export const LIVE_POLL_MS = 30_000;
/** Someone standing at the counter is worth a fresh number this often. */
export const PENDING_CARTS_POLL_MS = 30_000;
/** Stripe finishes a review without telling this app; the checklist notices on this cadence. */
export const PROVIDERS_POLL_MS = 30_000;
/** A location gains coordinates a moment after it is created; poll only while one is plausibly still in flight. */
export const GEOCODE_POLL_MS = 5_000;

/**
 * The signed-in area: loads the shops, follows the selection, keeps the
 * selected shop's branches and connections fresh, and mirrors what the app
 * shell shows on every page (the cart badge, the live indicator, the on-air
 * state). The onboarding gate reads `onboardingStep` from here.
 */
export class ShellController extends SubtreeController implements ShellActions {
  readonly state = new ShellState();
  #livePoll: ReturnType<typeof setInterval> | null = null;
  #cartsPoll: ReturnType<typeof setInterval> | null = null;
  #providersPoll: ReturnType<typeof setInterval> | null = null;
  #geocodePoll: ReturnType<typeof setInterval> | null = null;
  #pollingKey: string | null = null;
  /** The shop × location Inventory's last page belongs to: another counter starts Inventory afresh. */
  #inventoryKey: string | null = null;

  constructor(
    readonly deps: ShellDeps,
    readonly routing: ShellRouting,
  ) {
    super();
    this.subtree.put(ShellState, this.state);
    this.subtree.put(ShellActions, this);
    this.own(new SetupChecklistController(this.subtree, deps));

    this.sync(() => this.#reconcileStores(), [deps.storeRepository]);
    this.sync(() => this.#reconcileLocations(), [deps.storeRepository, deps.locationsRepository]);
    this.sync(() => this.#reconcileProviders(), [deps.storeRepository, deps.providersRepository]);
    this.sync(() => this.#reconcileCapabilities(), [deps.storeRepository, deps.capabilitiesRepository]);
    this.sync(() => {
      const repo = deps.pendingCartsRepository;
      const { shopId: storeId, locationId } = deps.storeRepository;
      const pos = storeId !== null && this.#has(storeId, "pointOfSale");
      // The count of the counter it was read for, and none where the counter takes no carts.
      this.state.pendingCarts.value = pos ? repo.countFor(shopEntryId(storeId, locationId)) : 0;
      if (repo.isStale && pos) void loadPendingCarts({ ...deps, storeId, locationId }).catch(() => undefined);
    }, [deps.pendingCartsRepository, deps.storeRepository, deps.capabilitiesRepository]);
    this.sync(() => {
      this.state.onAir.value = deps.broadcastStudio.onAir;
    }, [deps.broadcastStudio]);

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.#onVisibilityChange);
      this.autoDispose(() => document.removeEventListener("visibilitychange", this.#onVisibilityChange));
    }
    this.autoDispose(() => this.#stopPolling());
    this.autoDispose(() => setSentryStore(null));
  }

  signOut = async () => {
    await signOut(this.deps);
    if (this.isDisposed) return;
    this.routing.onSignedOut();
  };

  onboardingAnswered = () => {
    this.#reconcileLocations();
  };

  retryStores = () => {
    void loadStores(this.deps);
  };

  requestStore = (storeId: string | null) => {
    this.deps.storeRepository.requestStore(storeId);
  };

  switchWorkspace = () => {
    switchWorkspace(this.deps);
  };

  rememberInventory = (path: string) => {
    this.state.inventoryPath.value = path;
  };

  rememberCustomer = (path: string) => {
    this.state.customerPath.value = path;
  };

  #reconcileStores(): void {
    const repo = this.deps.storeRepository;
    // Sign-out resets the repository before the router unmounts the shell; a reload then would only 401.
    const signedIn = this.deps.session.stage === "signedIn";
    if (signedIn && (repo.status.kind === "idle" || repo.isStale)) void loadStores(this.deps);
    const store = repo.selectedStore;
    this.state.selectedStore.value = store;
    this.state.role.value = store?.myRole ?? null;
    this.state.storeError.value = repo.status.kind === "error" ? repo.status.message : null;
    setSentryStore(store?.id ?? null);
    this.#reconcilePolling();
  }

  /** Loaded once per shop; until it answers the shell shows no verdict, as it does while `role` is null. */
  #reconcileCapabilities(): void {
    const store = this.deps.storeRepository.selectedStore;
    const repo = this.deps.capabilitiesRepository;
    if (!store) {
      this.state.capabilities.value = null;
      this.state.capabilitiesFailed.value = false;
      this.#reconcilePolling();
      return;
    }
    const signedIn = this.deps.session.stage === "signedIn";
    if (signedIn && (repo.storeId !== store.id || repo.status.kind === "idle")) {
      void loadCapabilities({ ...this.deps, storeId: store.id });
    }
    const capabilities = repo.for(store.id);
    this.state.capabilities.value = capabilities;
    this.state.capabilitiesFailed.value = !capabilities && repo.storeId === store.id && repo.status.kind === "error";
    this.#reconcilePolling();
  }

  #has(storeId: string, flag: "pointOfSale" | "liveBreaks"): boolean {
    return this.deps.capabilitiesRepository.for(storeId)?.[flag] === true;
  }

  /** Restarts the polls when the counter changes, or when a capability that one of them needs flips. */
  #reconcilePolling(): void {
    const repo = this.deps.storeRepository;
    const storeId = repo.selectedStore?.id ?? null;
    const pos = storeId ? this.#has(storeId, "pointOfSale") : false;
    const live = storeId ? this.#has(storeId, "liveBreaks") : false;
    const key = storeId && repo.workspace ? `${storeId}:${repo.locationId ?? ""}:${pos}:${live}` : null;
    if (key !== this.#pollingKey) {
      this.#pollingKey = key;
      this.#restartPolling(key ? storeId : null);
    }
  }

  /**
   * The gate opens once the shops are known and the selected shop's branches
   * are too. A failed stores request is never read as "this user has no shop":
   * that would push an established merchant into the create-shop wizard over a blip.
   */
  #reconcileLocations(): void {
    const stores = this.deps.storeRepository;
    const locations = this.deps.locationsRepository;
    const store = stores.selectedStore;

    if (stores.status.kind === "error") {
      this.state.storeGate.value = "error";
      return;
    }
    if (stores.status.kind !== "loaded") {
      this.state.storeGate.value = "loading";
      return;
    }
    if (!store) {
      this.state.locationCount.value = 0;
      this.state.selectedLocation.value = null;
      this.state.needsWorkspace.value = false;
      this.state.canSwitchWorkspace.value = false;
      this.state.onboardingStep.value = resolveOnboardingStep({ store: null, locationCount: 0, role: null });
      this.state.storeGate.value = "ready";
      return;
    }
    const inFlight = locations.storeId === store.id && locations.status.kind === "loading";
    if (!locations.isFreshFor(store.id) && !inFlight) void loadLocations({ ...this.deps, storeId: store.id });
    if (!locations.isSettledFor(store.id)) {
      this.state.storeGate.value = "loading";
      return;
    }
    const count = locations.locations.length;
    this.state.locationCount.value = count;
    this.#reconcileWorkspace(store.id);
    const boothsOnly = count > 0 && locations.locations.every((l) => l.kind === "BOOTH");
    this.state.onboardingStep.value = resolveOnboardingStep({
      store,
      locationCount: count,
      boothsOnly,
      role: store.myRole,
    });
    this.state.storeGate.value = "ready";
    this.#watchGeocoding(store.id);
  }

  /** Takes the only shop × location there is without asking, and otherwise leaves the choice to the picker. */
  #reconcileWorkspace(storeId: string): void {
    const stores = this.deps.storeRepository;
    const all = this.deps.locationsRepository.locations;
    const active = activeLocations(all);
    this.state.canSwitchWorkspace.value = stores.stores.length > 1 || active.length > 1;
    if (!isWorkspaceValid(stores.workspace, all)) {
      const store = stores.find(storeId);
      const implicit = store ? implicitWorkspace(stores.stores, store, all) : null;
      if (!implicit) {
        this.state.selectedLocation.value = null;
        this.state.needsWorkspace.value = true;
        return;
      }
      chooseWorkspace({ ...this.deps, workspace: implicit });
    }
    this.state.selectedLocation.value = active.find((l) => l.id === stores.locationId) ?? null;
    this.state.needsWorkspace.value = false;
    const key = `${storeId}:${stores.locationId ?? ""}`;
    if (key !== this.#inventoryKey) {
      const changed = this.#inventoryKey !== null;
      this.#inventoryKey = key;
      this.state.inventoryPath.value = null;
      this.state.customerPath.value = null;
      if (changed) this.routing.leaveCustomer?.();
    }
  }

  /**
   * The store row is the fallback when the providers have nothing to say yet.
   * It carries the connection without the derived `merchantAction`, hence the
   * explicit null: with no live answer, nothing may claim Stripe is mid-review.
   */
  #reconcileProviders(): void {
    const store = this.deps.storeRepository.selectedStore;
    const repo = this.deps.providersRepository;
    if (!store) return;
    const inFlight = repo.storeId === store.id && repo.status.kind === "loading";
    if ((repo.storeId !== store.id || repo.status.kind === "idle" || repo.isStale) && !inFlight) {
      void loadProviders({ ...this.deps, storeId: store.id });
    }
  }

  /** Poll while a fresh branch has no coordinates yet; stop as soon as none is waiting. */
  #watchGeocoding(storeId: string): void {
    const waiting = this.deps.locationsRepository.awaitingGeocode;
    if (waiting && !this.#geocodePoll) {
      this.#geocodePoll = setInterval(() => {
        if (this.deps.storeRepository.shopId !== storeId) return;
        void loadLocations({ ...this.deps, storeId });
      }, GEOCODE_POLL_MS);
    } else if (!waiting && this.#geocodePoll) {
      clearInterval(this.#geocodePoll);
      this.#geocodePoll = null;
    }
  }

  #restartPolling(storeId: string | null): void {
    this.#stopPolling();
    if (!storeId) {
      this.state.liveNow.value = false;
      return;
    }
    this.#refresh(storeId);
    if (this.#has(storeId, "liveBreaks")) {
      this.#livePoll = setInterval(() => this.#refreshLive(storeId), LIVE_POLL_MS);
    }
    if (this.#has(storeId, "pointOfSale")) {
      this.#cartsPoll = setInterval(() => this.#refreshCarts(storeId), PENDING_CARTS_POLL_MS);
    }
    this.#providersPoll = setInterval(() => this.#refreshProviders(storeId), PROVIDERS_POLL_MS);
  }

  #stopPolling(): void {
    for (const timer of [this.#livePoll, this.#cartsPoll, this.#providersPoll, this.#geocodePoll]) {
      if (timer) clearInterval(timer);
    }
    this.#livePoll = null;
    this.#cartsPoll = null;
    this.#providersPoll = null;
    this.#geocodePoll = null;
  }

  #refresh(storeId: string): void {
    this.#refreshLive(storeId);
    this.#refreshCarts(storeId);
    this.#refreshProviders(storeId);
  }

  #refreshProviders(storeId: string): void {
    void loadProviders({ ...this.deps, storeId });
  }

  #refreshLive(storeId: string): void {
    if (!this.#has(storeId, "liveBreaks")) {
      this.state.liveNow.value = false;
      return;
    }
    const { locationId } = this.deps.storeRepository;
    void isStreamLive({ ...this.deps, storeId, locationId })
      .then((live) => {
        const repo = this.deps.storeRepository;
        // An answer for another counter says nothing of this one.
        if (this.isDisposed || live === null || repo.shopId !== storeId || repo.locationId !== locationId) return;
        this.state.liveNow.value = live;
      })
      // A read that throws keeps the dot as it was: the next poll asks again.
      .catch(() => undefined);
  }

  #refreshCarts(storeId: string): void {
    if (!this.#has(storeId, "pointOfSale")) return;
    void loadPendingCarts({ ...this.deps, storeId, locationId: this.deps.storeRepository.locationId }).catch(
      () => undefined,
    );
  }

  /** Coming back to the tab is when a stale badge is noticed, so it is when it is refreshed. */
  readonly #onVisibilityChange = (): void => {
    if (document.visibilityState !== "visible") return;
    const storeId = this.deps.storeRepository.shopId;
    if (!storeId) return;
    // Staff switch capabilities on without telling this app; coming back is when it is worth asking.
    void loadCapabilities({ ...this.deps, storeId });
    this.#refresh(storeId);
  };
}
